"use client";

import { ChevronRightIcon } from "@/components/event-detail/chevron-right-icon";
import { CancelAllConfirmDialog } from "@/components/order/cancel-all-confirm-dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Tabs } from "@/components/ui/tabs";
import { useUserInfo } from "@/store/user-info";
import { navigateToOrderGuessDetail, useAppRouter } from "@/utils/router";
import { get } from "@/utils/utils";
import { BigMath } from "@yn/utils";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { OrdersTab } from "./positions-panel-components/OrdersTab";
import { PositionsTab } from "./positions-panel-components/PositionsTab";
import { useCancelOrder } from "./positions-panel-hooks/use-cancel-order";
import { useCashOut } from "./positions-panel-hooks/use-cash-out";
import { useOrdersData } from "./positions-panel-hooks/use-orders-data";
import { usePositionsData } from "./positions-panel-hooks/use-positions-data";

/**
 * PositionsPanel 组件的属性
 */
interface PositionsPanelProps {
    /** 事件信息 */
    event: any;
    /** 竞猜编号 */
    guessNo: string;
}

/**
 * 标签页类型定义
 */
type PositionsTab = "position" | "orders";

/**
 * 持仓面板组件
 *
 * 功能：
 * - 显示用户的持仓、挂单两个标签页
 * - 支持 Cash Out 操作
 * - 支持取消挂单操作
 *
 * 架构：
 * - 使用自定义 hooks 管理数据获取和业务逻辑
 * - 使用子组件进行 UI 渲染，保持主组件简洁
 */
export function PositionsPanel( { event, guessNo }: PositionsPanelProps ) {
    const router = useAppRouter();
    const searchParams = useSearchParams();
    // 当前选中的标签页
    const [ positionTab, setPositionTab ] = useState<PositionsTab>( "position" );
    const [ hasManualSelection, setHasManualSelection ] = useState( false );
    const { userId } = useUserInfo();

    // 数据获取 hooks
    const { positionItems, isLoading: isPositionsLoading } = usePositionsData( guessNo, userId, true );
    const { ordersItems, isLoading: isOrdersLoading } = useOrdersData( guessNo, userId, true );
    // 业务逻辑 hooks - 取消订单
    const {
        confirmDialogOpen,
        setConfirmDialogOpen,
        isPending,
        handleCancelOrder,
        handleConfirmCancel,
        handleDirectCancelOrder,
        setPendingCancelOrder,
        cancelAllDialogOpen,
        setCancelAllDialogOpen,
        isPendingCancelAll,
        handleCancelAll,
        handleConfirmCancelAll,
    } = useCancelOrder( guessNo, userId );

    // 业务逻辑 hooks - Cash Out
    const { handleOpenCashOut } = useCashOut( event, guessNo, positionItems );

    const hasPositions = positionItems.length > 0;
    const hasOrders = ordersItems.length > 0;
    const isAnyLoading = isPositionsLoading || isOrdersLoading;
    const [ hasLoadedOnce, setHasLoadedOnce ] = useState( false );
    const [ hasAnyData, setHasAnyData ] = useState( false );
    const showPositionsLoading = !hasLoadedOnce && isPositionsLoading;
    const showOrdersLoading = !hasLoadedOnce && isOrdersLoading;
    const guessList = get( event, "guessList", [] );
    const currentGuess = ( Array.isArray( guessList ) ? guessList : [] ).find( ( g: any ) =>
        get( g, "guessNo", "" ) === guessNo
    );
    const eventNo = searchParams.get( "eventno" ) ?? get( event, "eventNo", "" );

    useEffect( () => {
        if ( hasManualSelection ) return;

        if ( hasPositions ) {
            setPositionTab( "position" );
            return;
        }
        if ( hasOrders ) {
            setPositionTab( "orders" );
        }
    }, [ hasManualSelection, hasPositions, hasOrders ] );

    useEffect( () => {
        if ( !isAnyLoading ) {
            setHasLoadedOnce( true );
        }
    }, [ isAnyLoading ] );

    useEffect( () => {
        if ( hasPositions || hasOrders ) {
            setHasAnyData( true );
        }
    }, [ hasPositions, hasOrders ] );
    const cancelAllSummary = useMemo( () => {
        let buyQty = 0;
        let buyValue = 0;
        let sellQty = 0;
        let sellValue = 0;

        ordersItems.forEach( ( order ) => {
            const qty = order.totalQuantity || 0;
            const value = BigMath.times( order.price || 0, qty );

            if ( order.direction === 1 ) {
                buyQty = BigMath.plus( buyQty, qty );
                buyValue = BigMath.plus( buyValue, value );
            } else if ( order.direction === 2 ) {
                sellQty = BigMath.plus( sellQty, qty );
                sellValue = BigMath.plus( sellValue, value );
            }
        } );

        return { buyQty, buyValue, sellQty, sellValue };
    }, [ ordersItems ] );

    if ( hasLoadedOnce && !hasAnyData ) {
        return null;
    }

    return (
        <div className="mt-3 rounded-xl bg-surface-card border border-stroke">
            <div className="flex items-center justify-between border-b border-stroke px-3">
                <Tabs
                    tabs={[ { value: "position", label: "Positions", disabled: !hasPositions }, {
                        value: "orders",
                        label: "Open Orders",
                        disabled: !hasOrders,
                    } ]}
                    activeTab={positionTab}
                    onTabChange={( value ) => {
                        setHasManualSelection( true );
                        setPositionTab( value as PositionsTab );
                    }}
                    className="w-auto flex-1 border-0 [&_[data-slot=tab-item]]:h-10 [&_[data-slot=tab-item]]:text-sm [&_[data-slot=tab-item]]:font-medium [&_[data-slot=tab-item]]:leading-normal [&_[data-active=true]]:font-semibold [&_[data-active=true]]:text-t1 [&_[data-active=false]]:font-medium [&_[data-active=false]]:text-t3 [&_[data-disabled=true]]:cursor-not-allowed [&_[data-disabled=true]]:opacity-40"
                />
                {positionTab === "position" && positionItems.length > 0 && (
                    <button
                        type="button"
                        onClick={() => {
                            if ( guessNo && eventNo ) {
                                navigateToOrderGuessDetail( router, {
                                    eventNo,
                                    guessGroupNo: get( currentGuess, "guessGroupNo", "" ),
                                    guessNo,
                                } );
                            }
                        }}
                        className="text-sm font-medium text-t1 whitespace-nowrap h-10 flex items-center gap-0.5"
                    >
                        +{positionItems.length} More
                        <ChevronRightIcon className="size-6 text-t3" />
                    </button>
                )}
                {positionTab === "orders" && ordersItems.length > 0 && (
                    <button
                        type="button"
                        onClick={handleCancelAll}
                        className="text-sm font-medium text-state-danger whitespace-nowrap h-10 flex items-center"
                    >
                        Cancel all
                    </button>
                )}
            </div>

            <div className="px-3 pb-3">
                {positionTab === "position" && ( showPositionsLoading
                    ? (
                        <div className="flex items-center justify-center py-8">
                            <LoadingSpinner size="lg" />
                        </div>
                    )
                    : (
                        <PositionsTab
                            positionItems={positionItems}
                            event={event}
                            guessNo={guessNo}
                            eventNo={eventNo}
                            onOpenCashOut={handleOpenCashOut}
                            currentGuess={currentGuess}
                        />
                    ) )}
                {positionTab === "orders" && ( showOrdersLoading
                    ? (
                        <div className="flex items-center justify-center py-8">
                            <LoadingSpinner size="lg" />
                        </div>
                    )
                    : (
                        <OrdersTab
                            ordersItems={ordersItems}
                            onCancelOrder={handleDirectCancelOrder}
                            onCancelAll={handleCancelAll}
                            event={event}
                            guessNo={guessNo}
                            eventNo={eventNo}
                            currentGuess={currentGuess}
                        />
                    ) )}
            </div>
            <ConfirmDialog
                open={confirmDialogOpen}
                onOpenChange={( open ) => {
                    setConfirmDialogOpen( open );
                    if ( !open ) {
                        setPendingCancelOrder( null );
                    }
                }}
                title="Are you sure?"
                description="Are you sure you want to cancel this open order?"
                confirmText="Confirm"
                cancelText="Never mind"
                onConfirm={handleConfirmCancel}
                isLoading={isPending}
            />
            <CancelAllConfirmDialog
                open={cancelAllDialogOpen}
                onOpenChange={setCancelAllDialogOpen}
                summary={cancelAllSummary}
                onConfirm={handleConfirmCancelAll}
                isLoading={isPendingCancelAll}
            />
        </div>
    );
}
